"use client";

import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import { Check, Eye, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ViewChangesDialog from "@/components/shared/view-changes-dialog";
import type { ViewChangesIdNameMaps } from "@/types/view-changes";

interface UnsavedChangesAlertProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onUndo: () => void;
  onSave: (e?: FormEvent) => void;
  className?: string;
  /**
   * Inset the bar from the parent panel edges so it lines up with
   * content cards (e.g. 24 for pages that use `p-6`).
   */
  contentInset?: number;
  bottomOffset?: number;
  compact?: boolean;
  before?: unknown;
  after?: unknown;
  fieldLabels?: Record<string, string>;
  idNameMaps?: ViewChangesIdNameMaps;
  /** Lazy snapshot when View changes is opened (preferred for Channels). */
  getSnapshot?: () => { before: unknown; after: unknown } | null;
}

export function UnsavedChangesAlert({
  hasUnsavedChanges,
  isSaving,
  onUndo,
  onSave,
  className,
  contentInset = 0,
  bottomOffset = 0,
  compact = false,
  before,
  after,
  fieldLabels,
  idNameMaps,
  getSnapshot,
}: UnsavedChangesAlertProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [barStyle, setBarStyle] = useState<CSSProperties>({});
  const [viewOpen, setViewOpen] = useState(false);
  const [dialogBefore, setDialogBefore] = useState<unknown>(undefined);
  const [dialogAfter, setDialogAfter] = useState<unknown>(undefined);

  const canViewChanges =
    Boolean(getSnapshot) || (before !== undefined && after !== undefined);

  const handleOpenViewChanges = () => {
    if (getSnapshot) {
      const snapshot = getSnapshot();
      if (!snapshot) return;
      setDialogBefore(snapshot.before);
      setDialogAfter(snapshot.after);
    } else {
      setDialogBefore(before);
      setDialogAfter(after);
    }
    setViewOpen(true);
  };

  useLayoutEffect(() => {
    if (compact) return;
    const panel = anchorRef.current?.parentElement;
    if (!panel) return;

    const update = () => {
      const rect = panel.getBoundingClientRect();
      const inset = Math.max(0, contentInset);
      const width = Math.max(0, rect.width - inset * 2);

      setBarStyle({
        left: rect.left + inset,
        width,
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [hasUnsavedChanges, contentInset, compact]);

  return (
    <>
      <div
        ref={anchorRef}
        className="pointer-events-none absolute h-0 w-0"
        aria-hidden
      />
      <div
        className={cn(
          "[&+*]:!mt-0 pointer-events-none fixed z-50",
          compact
            ? "bottom-4 left-1/2 w-max -translate-x-1/2 px-3 sm:bottom-6"
            : bottomOffset === 0
              ? "bottom-4 sm:bottom-6"
              : undefined,
          !compact && (contentInset > 0 ? "px-0" : "px-3 sm:px-0"),
          className
        )}
        style={
          compact
            ? bottomOffset === 0
              ? undefined
              : { bottom: `calc(1rem + ${bottomOffset}px)` }
            : bottomOffset === 0
              ? barStyle
              : { ...barStyle, bottom: `calc(1rem + ${bottomOffset}px)` }
        }
        aria-hidden={!hasUnsavedChanges}
      >
        <div
          className={cn(
            "border border-border bg-card shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_12px_40px_-4px_rgba(0,0,0,0.18)] transition-all duration-300 ease-out dark:border-border dark:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.4),0_16px_48px_-4px_rgba(0,0,0,0.55)]",
            compact
              ? "flex w-max items-center gap-2 rounded-full px-2 py-1.5 sm:gap-2.5 sm:px-3"
              : "flex w-full flex-col gap-3 rounded-2xl px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5",
            hasUnsavedChanges
              ? "pointer-events-auto translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          )}
        >
          {compact ? (
            <>
              <span className="size-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
              <p className="sr-only">Unsaved changes</p>
            </>
          ) : (
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="size-2.5 shrink-0 rounded-full bg-amber-500" aria-hidden />
              <p className="truncate text-sm font-medium text-foreground">Unsaved changes</p>
            </div>
          )}

          <div
            className={cn(
              compact
                ? "flex shrink-0 items-center gap-1.5"
                : "grid min-w-0 grid-cols-3 gap-2 sm:flex sm:shrink-0 sm:items-center sm:gap-2.5"
            )}
          >
            {canViewChanges && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenViewChanges}
                disabled={isSaving}
                className="h-9 min-w-0 gap-1.5 rounded-full px-2 sm:gap-2 sm:px-4"
              >
                <Eye className="size-4 shrink-0" />
                <span className="truncate sm:hidden">View</span>
                <span className="hidden sm:inline">View changes</span>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onUndo}
              disabled={isSaving}
              className={cn(
                "h-9 min-w-0 gap-1.5 rounded-full px-2 sm:gap-2 sm:px-4",
                !canViewChanges && "col-span-1"
              )}
            >
              <RotateCcw className="size-4 shrink-0" />
              <span className="truncate">Discard</span>
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isSaving}
              onClick={(e) => onSave(e)}
              className={cn(
                "h-9 min-w-0 gap-1.5 rounded-full px-2 sm:gap-2 sm:px-5",
                !canViewChanges && "col-span-2"
              )}
            >
              {isSaving ? (
                <Loader2 className="size-4 shrink-0 animate-spin" />
              ) : (
                <Check className="size-4 shrink-0" />
              )}
              <span className="truncate sm:hidden">
                {isSaving ? "Saving…" : "Save"}
              </span>
              <span className="hidden sm:inline">
                {isSaving ? "Saving..." : "Save changes"}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <ViewChangesDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        before={dialogBefore}
        after={dialogAfter}
        fieldLabels={fieldLabels}
        idNameMaps={idNameMaps}
      />
    </>
  );
}
